import React, { ReactNode, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { useQuery } from '@tanstack/react-query';

import { ArtifactTypeNameMapping } from '@/pages/UserAccount/UserRequests/lib';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { FormDocument, FormDocumentAttachment } from '@/features/ui/DocumentsData/model/types';
import { deleteAttachmentFiles, getFileNamesPreviewValue } from '@/features/ui/DocumentsData/ui/utils';
import { ConfirmInsuranceView } from '@/features/ui/Steps/InsuranceData/ui/ConfirmInsuranceView';
import { SelectInsuranceView } from '@/features/ui/Steps/InsuranceData/ui/SelectInsuranceView';

import { ConditionView, FormSelect, Grid, StepControlPanel, Tabs } from '@/shared/ui';

import { YesNoSelectOptions } from '@/components/Forms/constants';
import { Loader } from '@/shared/ui';

import { ResponseInsuranceOrder } from '@/modules/bia/shared/model/types';
import { getERZDocumentsSelect } from '@/modules/erz/helpers';

import { formatDate } from '@/helpers/dates';

import { queryKeys } from '@/api/queryKeys';
import { DocumentTypeCode, FormDataType } from '@/api/types';

import api from '@/api';

import { tabsContent } from './lib/utils';
import { InsuranceDataSchema, InsuranceTabKeys } from './model/types';

import './InsuranceData.scss';
import isEmpty from 'lodash/isEmpty';

export interface InsuranceProps {
  onDraftCreate?: (data?: { stateForm: FormDataType }) => Promise<string>;
  isInsuranceMandatory: boolean;
}

export const InsuranceData: React.FC<StepProps<InsuranceProps>> = ({
  next,
  prev,
  currentRequestId,
  serviceCode,
  isInsuranceMandatory = false,
}) => {
  const { control, watch, setValue } = useFormContext<InsuranceDataSchema>();

  const { isLoading: isInsuranceOrdersLoading, data: insuranceOrders } = useQuery({
    queryKey: queryKeys.insuranceOrders.list(),
    queryFn: () => api.bia.insuranceOrders(true).then((r) => r.data),
  });

  const { isLoading: isDocumentMetadataLoading, data: insuranceDocumentMetadata } = useQuery({
    queryKey: [...queryKeys.erz.documentTypes(), DocumentTypeCode.INSURANCE_POLICY],
    queryFn: () => getERZDocumentsSelect({ allowedDocumentTypeCodes: [DocumentTypeCode.INSURANCE_POLICY] }),
  });

  const isLoading = isInsuranceOrdersLoading || isDocumentMetadataLoading;

  const confirmInsuranceDoc = watch('confirmInsuranceDoc');
  const confirmInsuranceActiveTabKey = watch('confirmInsuranceActiveTabKey');
  const confirmInsuranceDocumentsWatch = watch('confirmInsuranceDocuments');
  const { remove } = useFieldArray({ control, name: 'confirmInsuranceDocuments' });

  const clearInsurances = (): void => {
    if (confirmInsuranceDocumentsWatch && currentRequestId) {
      deleteAttachmentFiles(confirmInsuranceDocumentsWatch, currentRequestId);
    }
    remove();
    setValue('confirmInsuranceDocuments', undefined);
    setValue('selectedInsurances', []);
  };

  useEffect(() => {
    if (!confirmInsuranceActiveTabKey) {
      setValue('confirmInsuranceActiveTabKey', InsuranceTabKeys.MANUAL);
    }

    if (isEmpty(confirmInsuranceDocumentsWatch)) {
      clearInsurances();
    }
  }, []);

  useEffect(() => {
    setValue('insuranceDocumentMetadata', insuranceDocumentMetadata?.[0]);
  }, [insuranceDocumentMetadata]);

  const showInsurance = isInsuranceMandatory || confirmInsuranceDoc?.id === YesNoSelectOptions.YES.id;

  return (
    <>
      <FormBlock title="Дані про страхування" className="insurance">
        <ConditionView if={!isInsuranceMandatory}>
          <FormSelect
            name="confirmInsuranceDoc"
            label="Ви хочете додати дані документа?"
            control={control}
            options={Object.values(YesNoSelectOptions)}
            defaultValue={YesNoSelectOptions.NO}
            required
            onChange={clearInsurances}
          />
        </ConditionView>
        <ConditionView if={showInsurance}>
          <>
            <Controller
              name="confirmInsuranceActiveTabKey"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Tabs
                  activeKey={value}
                  items={tabsContent(insuranceOrders?.length)}
                  onChange={(key) => {
                    clearInsurances();
                    onChange(key);
                  }}
                  className="insurance__tabs"
                />
              )}
            />
            <ConditionView
              if={isLoading}
              else={
                <ConditionView
                  if={confirmInsuranceActiveTabKey === InsuranceTabKeys.MANUAL}
                  else={
                    <SelectInsuranceView
                      serviceCode={serviceCode}
                      insuranceOrders={insuranceOrders ?? []}
                      currentRequestId={currentRequestId}
                    />
                  }
                >
                  <ConfirmInsuranceView
                    insuranceDocumentMetadata={insuranceDocumentMetadata ?? []}
                    currentRequestId={currentRequestId}
                  />
                </ConditionView>
              }
            >
              <Loader centered />
            </ConditionView>
          </>
        </ConditionView>
      </FormBlock>
      <StepControlPanel prev={prev} next={next} />
    </>
  );
};

export const InsurancePreview: React.FC = () => {
  const { getValues } = useFormContext<InsuranceDataSchema>();
  const { confirmInsuranceActiveTabKey, confirmInsuranceDocuments, selectedInsurances } = getValues();
  let data = [];

  if (confirmInsuranceActiveTabKey === InsuranceTabKeys.MANUAL) {
    data = (confirmInsuranceDocuments ?? []).reduce(
      (result: Array<[string, Nullable<string | ReactNode>]>, insurance: FormDocument) => {
        const { typeDoc, fileNames } = insurance.attachments as FormDocumentAttachment;

        return result.concat([
          ['Тип документу, який підтверджує наявність страхування', typeDoc],
          ['Серія та номер', insurance.info?.serialNumberDoc as string],
          ['Дата видачі', formatDate(insurance.info?.date as Date)],
          ['Термін дії', formatDate(insurance.info?.expirationDate as Date)],
          ['Документ про страхування', getFileNamesPreviewValue(fileNames)],
        ]);
      },
      [],
    );
  } else {
    data = selectedInsurances?.reduce(
      (result: Array<[string, Nullable<string | ReactNode>]>, insurance: ResponseInsuranceOrder) => {
        return result.concat([
          ['Тип документу, який підтверджує наявність страхування', ArtifactTypeNameMapping[insurance.artifacts[0]]],
          ['Серія та номер', insurance.number],
          ['Дата видачі', formatDate(insurance.date_from)],
          ['Термін дії', formatDate(insurance.date_to)],
        ]);
      },
      [],
    );
  }

  return <Grid title="Дані документу про страхування" data={data ?? []} />;
};
